import { FC, useEffect, useMemo, useRef, useState } from 'react';
import cx from 'classnames';
import Image from "next/image";
import { useRouter } from 'next/router';
import { DEFAULT_BLUR_IMAGE_DATA_URL } from 'constants/blurImage';
import { BackgroundColor, generateGlassesImage, generateSparePartImage } from 'utils/image.utils';
import { slugToString, stringToSlug } from 'functions/slug';
import { SwiperSlide, Swiper } from 'swiper/react';
import { Pagination } from 'swiper';
import 'swiper/css';
import 'swiper/css/pagination';
import { useHover } from "@uidotdev/usehooks";
import useWindowSize from 'hooks/useWindowSize';
import { useDispatch, useSelector } from 'react-redux';
import { setLoading, setPeopleAlsoBoughtModal } from 'redux/features/bag/bagSlice';
import { addToCart, setPeopleAlsoBought } from 'redux/features/cart/cartSlice';
import { v4 as uuidv4 } from 'uuid';
import Spinner from 'components/Spinner/Spinner';
import { RootState } from 'redux/store';
import { frameSizeConst } from 'constants/frameSize';
import { imageLoader } from 'functions/imageLoader';
import { getSession } from 'functions/session';

const CartArmRecommended = ({
    product,
}: { product: any }) => {
    const color = useMemo(() => {
        return stringToSlug(product.attributes.nodes[0].value);
    }, [product])

    const productName = useMemo(() => {
        return product && product ? product.name.split(',')[0] : 'Arms - ' + slugToString(color);
    }, [product, color]);

    const [salePrice, setSalePrice] = useState(0);
    const [regularPrice, setRegularPrice] = useState(0);

    useEffect(() => {
        if (product) {
            setSalePrice(Number(product.price.split('&nbsp;')[1]));
            setRegularPrice(Number(product.regularPrice.split('&nbsp;')[1]));
        }
    }, [product]);

    const [hoverRef, isHover] = useHover();

    const { width } = useWindowSize();
    const dispatch = useDispatch();
    const uuidV4 = uuidv4();
    const isLoad = useSelector((state: RootState) => state.bagEvent.loading);

    const getRegularSize = (size: string) => {
        var sizeTitle;
        frameSizeConst.forEach((frameSize: any) => {
            if (frameSize.title === size) {
                sizeTitle = frameSize.short;
            }
        });
        return sizeTitle;
    }
    const router = useRouter();
    return (
        <>
            <div
                ref={hoverRef as any}
                className="group flex h-full flex-col rounded-[8px] bg-white p-[8px] text-brand-black100 transition-shadow duration-500 sm:p-[20px] sm:pb-[24px] sm:hover:shadow-card">
                <div className='cursor-pointer' onClick={() => {
                    router.push(`/shop/spare-parts/${stringToSlug(color)}`);
                }}>
                    {width >= 1140 ? (
                        <div className="h-[275px] w-full">
                            <div
                                className={`relative h-[240px] w-full animate-opacityinquick ${isHover ? 'block' : 'hidden'
                                    }`}>
                                <Image
                                    quality={80}
                                    fill
                                    style={{
                                        objectFit: "contain",
                                    }}
                                    sizes="230px"
                                    loader={imageLoader}
                                    src={generateGlassesImage(product.frame_color, color, '1', 'clear', BackgroundColor.White)}
                                    onError={(ev) => ev.currentTarget.src = 'https://cdn.dresden.vision/images/noimage.png'}
                                    title={productName}
                                    alt={productName}
                                />
                            </div>
                            <div
                                className={`relative h-[240px] w-full animate-opacityinquick ${isHover ? 'hidden' : 'block'
                                    }`}>
                                <Image
                                    quality={90}
                                    fill
                                    style={{
                                        objectFit: "contain",
                                    }}
                                    sizes="230px"
                                    loader={imageLoader}
                                    placeholder="blur"
                                    blurDataURL={DEFAULT_BLUR_IMAGE_DATA_URL}
                                    src={generateSparePartImage(color, BackgroundColor.White)}
                                    title={productName}
                                    alt={productName}
                                />
                            </div>
                        </div>
                    ) : (
                        <div className="relative h-[234px] w-full smx:h-[234px]">
                            <Swiper modules={[Pagination]} pagination>
                                <SwiperSlide>
                                    <div className="relative h-[234px] w-full smx:h-[234px]">
                                        <Image
                                            quality={90}
                                            placeholder="blur"
                                            blurDataURL={DEFAULT_BLUR_IMAGE_DATA_URL}
                                            fill
                                            style={{
                                                objectFit: "cover",
                                            }}
                                            sizes="230px"
                                            loader={imageLoader}
                                            src={generateSparePartImage(color, BackgroundColor.White)}
                                            alt={productName}
                                            title={productName}
                                        />
                                    </div>
                                </SwiperSlide>
                                <SwiperSlide>
                                    <div className="relative h-[234px] w-full smx:h-[234px]">
                                        <Image
                                            quality={90}
                                            placeholder="blur"
                                            blurDataURL={DEFAULT_BLUR_IMAGE_DATA_URL}
                                            fill
                                            style={{
                                                objectFit: "cover",
                                            }}
                                            sizes="230px"
                                            loader={imageLoader}
                                            src={generateGlassesImage(product.frame_color, color, '1', 'clear', BackgroundColor.White)}
                                            alt={productName}
                                            title={productName}
                                        />
                                    </div>
                                </SwiperSlide>
                            </Swiper>
                        </div>
                    )}
                </div>
                <div className="flex flex-auto flex-col items-center justify-between">
                    <span className="text-16px mt-[8px] flex-auto text-center leading-[17px]">
                        {productName} ({getRegularSize(product.attributes.nodes[1].value)})
                    </span>
                    <span className="mt-[8px] font-medium">
                        {salePrice != regularPrice ? (
                            <>
                                <span className="text-12px line-through text-brand-grey400 mr-[8px]">
                                    ${regularPrice}
                                </span>
                                <span
                                    className={cx({
                                        'text-brand-green': salePrice != regularPrice
                                    })} dangerouslySetInnerHTML={{ __html: '$' + (salePrice) }}>
                                </span>
                            </>
                        ) : (
                            <span dangerouslySetInnerHTML={{ __html: '$' + (salePrice) }}></span>
                        )}
                    </span>
                    <span onClick={() => {
                        const session = getSession();
                        if (session) {
                            dispatch(setLoading(true));
                            dispatch(addToCart({
                                type: 'simple',
                                variables: {
                                    queueId: uuidV4,
                                    input: {
                                        productId: product.databaseId,
                                        clientMutationId:
                                            getSession(),
                                        quantity: 1
                                    }
                                }
                            }));
                            dispatch(setPeopleAlsoBought(true));
                        }
                    }} className="mt-[16px] h-[40px] w-[140px] cursor-pointer rounded-[6px] border border-brand-grey400 text-center font-medium leading-[35px] transition-colors duration-500 sm:group-hover:text-brand-orange">
                        <span className="flex items-center  justify-center ">
                            {isLoad && (
                                <span className="mr-2 mt-[1px]">
                                    <Spinner fill="black" />
                                </span>
                            )}
                            <span>
                                Add to cart
                            </span>
                        </span>

                    </span>
                </div>
            </div>
        </>
    );
};

export default CartArmRecommended;
